import { createTheme } from '@mui/material/styles';

// Extend the theme to include custom colors
declare module '@mui/material/styles' {
  interface Palette {
    giftCode: {
      applied: string;
    };
  }

  interface PaletteOptions {
    giftCode?: {
      applied?: string;
    };
  }
}

// Colors from mobile app
export const colors = {
  black: '#101010',
  primaryLightGrey: '#9A9AB1',
  primaryMediumGrey: '#41415E',
  primaryDarkGrey: '#191928',
  primaryPurpleLight: '#DEA9FF',
  primaryPurpleMedium: '#B855FF',
  primaryPurpleDark: '#AD3BFF',
  primaryPurpleDarker: '#6E00B4',
  primaryWhite: '#EEEEEE',
  folly: '#FF3F7E',
  carmine: '#D80048',
  neutralVariant175: '#d0d0d0',
  neutralVariant225: '#BDBDBD',
  slateLavender: '#75749B',
  giftCodeApplied: '#E597DC',
};

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: colors.primaryPurpleLight,
      dark: colors.primaryPurpleDark,
      light: colors.primaryPurpleMedium,
    },
    secondary: {
      main: colors.folly,
    },
    background: {
      default: colors.black,
      paper: colors.primaryDarkGrey,
    },
    text: {
      primary: colors.primaryWhite,
      secondary: colors.primaryLightGrey,
    },
    error: {
      main: '#FF608B',
    },
    // Custom color for gift code
    giftCode: {
      applied: colors.giftCodeApplied,
    },
  },
  typography: {
    fontFamily: 'Nunito Sans, sans-serif',
    h1: {
      fontSize: '32px',
      fontWeight: 800,
      letterSpacing: '-0.5px',
      lineHeight: 1.1,
    },
    h2: {
      fontSize: '24px',
      fontWeight: 800,
      letterSpacing: '-0.25px',
      lineHeight: 1.1,
    },
    h3: {
      fontSize: '20px',
      fontWeight: 800,
      letterSpacing: '-0.25px',
      lineHeight: 1.1,
    },
    h4: {
      fontSize: '18px',
      fontWeight: 700,
      letterSpacing: '-0.25px',
      lineHeight: 1.1,
    },
    h5: {
      fontSize: '16px',
      fontWeight: 700,
      letterSpacing: '-0.25px',
      lineHeight: 1.1,
    },
    h6: {
      fontSize: '14px',
      fontWeight: 700,
      letterSpacing: '-0.25px',
      lineHeight: 1.1,
    },
    body1: {
      fontSize: '16px',
      fontWeight: 400,
      letterSpacing: '-0.25px',
      lineHeight: 1.25,
    },
    body2: {
      fontSize: '14px',
      fontWeight: 400,
      letterSpacing: '-0.25px',
      lineHeight: 1.25,
    },
    button: {
      fontSize: '16px',
      fontWeight: 700,
      letterSpacing: '-0.25px',
      lineHeight: 1.25,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '24px',
          padding: '12px 24px',
          textTransform: 'none',
        },
        contained: {
          backgroundColor: colors.primaryPurpleDark,
          color: colors.primaryWhite,
          '&:hover': {
            backgroundColor: colors.primaryPurpleMedium,
            color: colors.primaryWhite,
          },
        },
        outlined: {
          borderColor: colors.primaryPurpleLight,
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
            '& fieldset': {
              borderColor: colors.primaryLightGrey,
              borderWidth: '2px',
            },
            '&:hover fieldset': {
              borderColor: colors.primaryPurpleLight,
            },
            '&.Mui-focused:not(.Mui-error) fieldset': {
              borderColor: colors.primaryPurpleLight,
              borderWidth: '2px',
            },
            // Error state should override focused state - use error color directly
            '&.Mui-error': {
              '& fieldset': {
                borderColor: '#FF608B',
              },
              '&:hover fieldset': {
                borderColor: '#FF608B',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#FF608B',
                borderWidth: '2px',
              },
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: colors.primaryDarkGrey,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.primaryDarkGrey,
          borderRadius: '12px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.primaryDarkGrey,
          borderRadius: '12px',
        },
      },
    },
  },
});

export default theme; 